/**
 * Voice AI — Agent One Memory Tab
 * Shows past conversations, memory facts, and search.
 * Only visible when agent.isAgentOne === true.
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';

export class AgentMemoryTab extends LitElement {
  static properties = {
    agent: { type: Object },
    _conversations: { type: Array, state: true },
    _facts: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _expandedConvo: { type: Number, state: true },
    _search: { type: String, state: true },
    _searchResults: { type: Array, state: true },
    _convPage: { type: Number, state: true },
    _convTotal: { type: Number, state: true },
    _factPage: { type: Number, state: true },
    _factTotal: { type: Number, state: true },
  };

  static PAGE_SIZE = 20;

  static styles = [designSystem, componentStyles, agentStyles, css`
    .mem-section {
      background: var(--color-bg);
      border: 1px solid var(--color-border);
      border-radius: 12px;
      padding: 20px 24px;
      margin-bottom: 20px;
    }
    .mem-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--color-text-primary);
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .mem-desc {
      font-size: 13px;
      color: var(--color-text-tertiary);
      margin-bottom: 16px;
    }

    /* Search */
    .mem-search {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
    }
    .mem-search input {
      flex: 1;
      padding: 10px 14px;
      border: 1px solid var(--color-border);
      border-radius: 10px;
      font-size: 13px;
    }
    .mem-search input:focus { border-color: #46A546; outline: none; box-shadow: 0 0 0 3px rgba(70,165,70,0.1); }

    /* Conversations */
    .convo-list { display: flex; flex-direction: column; gap: 8px; }
    .convo-item {
      border: 1px solid var(--color-border);
      border-radius: 10px;
      overflow: hidden;
      transition: border-color 0.15s ease;
    }
    .convo-item:hover { border-color: #bbd7b5; }
    .convo-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      cursor: pointer;
      background: #FDFCFB;
      user-select: none;
    }
    .convo-header:hover { background: #F5F2F0; }
    .convo-date {
      font-size: 13px;
      font-weight: 600;
      color: var(--color-text-primary);
      min-width: 140px;
    }
    .convo-summary {
      flex: 1;
      font-size: 12px;
      color: var(--color-text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .convo-duration {
      font-size: 11px;
      color: var(--color-text-tertiary);
      background: var(--color-bg-secondary);
      padding: 2px 8px;
      border-radius: 100px;
      white-space: nowrap;
    }
    .convo-chevron {
      font-size: 11px;
      color: var(--color-text-tertiary);
      transition: transform 0.2s ease;
    }
    .convo-chevron.open { transform: rotate(90deg); }
    .convo-body {
      padding: 12px 16px;
      border-top: 1px solid var(--color-border);
      background: #fff;
    }
    .convo-transcript {
      max-height: 300px;
      overflow-y: auto;
      font-size: 12px;
      line-height: 1.6;
    }
    .convo-transcript .msg {
      margin-bottom: 6px;
      padding: 6px 10px;
      border-radius: 8px;
    }
    .convo-transcript .msg.user { background: #E8F0FE; margin-left: 20%; text-align: right; }
    .convo-transcript .msg.assistant { background: #F0F0F0; margin-right: 20%; }
    .convo-actions {
      display: flex;
      gap: 8px;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--color-border);
      font-size: 12px;
    }
    .convo-topics {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      margin-top: 6px;
    }
    .topic-tag {
      font-size: 10px;
      padding: 2px 8px;
      border-radius: 100px;
      background: #e8f4fc;
      color: #1e6a8a;
    }

    /* Facts */
    .facts-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .fact-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid var(--color-border);
      border-radius: 8px;
      font-size: 12px;
    }
    .fact-category {
      font-size: 9px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 100px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      flex-shrink: 0;
    }
    .fact-category.action { background: #e8f4fc; color: #1e6a8a; }
    .fact-category.decision { background: #fef7e0; color: #8B6914; }
    .fact-category.issue { background: #fef2f2; color: #a43333; }
    .fact-category.note { background: #F5F2F0; color: #6b7280; }
    .fact-category.preference { background: #e8fce8; color: #2C6C11; }
    .fact-content { flex: 1; }
    .fact-key { font-weight: 600; color: var(--color-text-primary); }
    .fact-value { color: var(--color-text-secondary); margin-top: 2px; }

    .empty-state {
      text-align: center;
      padding: 32px;
      color: var(--color-text-tertiary);
      font-size: 13px;
    }
    .empty-state i { font-size: 32px; margin-bottom: 12px; display: block; opacity: 0.3; }

    /* Pagination */
    .mem-pagination {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid var(--color-border);
    }
    .mem-pagination-info {
      font-size: 12px;
      color: var(--color-text-tertiary);
    }
    .mem-pagination-info strong { color: var(--color-text-primary); font-weight: 600; }
    .mem-pagination-controls {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .mem-page-btn {
      min-width: 30px;
      height: 30px;
      padding: 0 8px;
      border: 1px solid var(--color-border);
      border-radius: 6px;
      background: #fff;
      color: var(--color-text-secondary);
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .mem-page-btn:hover:not(:disabled) {
      border-color: #bbd7b5;
      color: var(--color-text-primary);
    }
    .mem-page-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .mem-page-btn.active {
      background: #5B8A72;
      border-color: #5B8A72;
      color: #fff;
    }
    .mem-page-ellipsis {
      padding: 0 4px;
      color: var(--color-text-tertiary);
      font-size: 12px;
    }

    @media (max-width: 768px) {
      .facts-grid { grid-template-columns: 1fr; }
      .convo-summary { display: none; }
      .mem-pagination { flex-direction: column; align-items: flex-start; }
    }
  `];

  constructor() {
    super();
    this._conversations = [];
    this._facts = [];
    this._loading = false;
    this._expandedConvo = null;
    this._search = '';
    this._searchResults = null;
    this._lastAgentId = null;
    this._convPage = 1;
    this._convTotal = 0;
    this._factPage = 1;
    this._factTotal = 0;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._convPage = 1;
      this._factPage = 1;
      this._loadMemory();
    }
  }

  async _loadMemory() {
    if (!this.agent?.id) return;
    this._loading = true;
    try {
      const url = `/voice/php/api/agent-memory.php`
        + `?agent_id=${this.agent.id}`
        + `&conv_page=${this._convPage}&conv_limit=${AgentMemoryTab.PAGE_SIZE}`
        + `&fact_page=${this._factPage}&fact_limit=${AgentMemoryTab.PAGE_SIZE}`;
      const resp = await authService.apiFetch(url);
      const data = await resp.json();
      if (data.success) {
        this._conversations = data.conversations || [];
        this._facts = data.facts || [];
        this._convTotal = data.conv_total || 0;
        this._factTotal = data.fact_total || 0;
      }
    } catch (e) {
      console.error('Failed to load agent memory:', e);
    }
    this._loading = false;
  }

  _goToConvPage(page) {
    const totalPages = Math.max(1, Math.ceil(this._convTotal / AgentMemoryTab.PAGE_SIZE));
    page = Math.max(1, Math.min(totalPages, page));
    if (page === this._convPage) return;
    this._convPage = page;
    this._expandedConvo = null;
    this._loadMemory();
  }

  _goToFactPage(page) {
    const totalPages = Math.max(1, Math.ceil(this._factTotal / AgentMemoryTab.PAGE_SIZE));
    page = Math.max(1, Math.min(totalPages, page));
    if (page === this._factPage) return;
    this._factPage = page;
    this._loadMemory();
  }

  _renderPagination(currentPage, total, onPageChange) {
    const size = AgentMemoryTab.PAGE_SIZE;
    const totalPages = Math.ceil(total / size);
    if (totalPages <= 1) return '';

    const startIdx = (currentPage - 1) * size;
    const endIdx = Math.min(startIdx + size, total);

    // Compact page number list: [1] [...] [cur-1] [cur] [cur+1] [...] [last]
    const pages = [];
    const add = (n) => pages.push(n);
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) add(i);
    } else {
      add(1);
      if (currentPage > 3) add('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) add(i);
      if (currentPage < totalPages - 2) add('...');
      add(totalPages);
    }

    return html`
      <div class="mem-pagination">
        <div class="mem-pagination-info">
          Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${total.toLocaleString()}</strong>
        </div>
        <div class="mem-pagination-controls">
          <button class="mem-page-btn" ?disabled=${currentPage <= 1}
            @click=${() => onPageChange(currentPage - 1)}
            aria-label="Previous page">
            <i class="fa-sharp fa-light fa-chevron-left"></i>
          </button>
          ${pages.map(p => p === '...'
            ? html`<span class="mem-page-ellipsis">&hellip;</span>`
            : html`<button class="mem-page-btn ${p === currentPage ? 'active' : ''}"
                @click=${() => onPageChange(p)}>${p}</button>`
          )}
          <button class="mem-page-btn" ?disabled=${currentPage >= totalPages}
            @click=${() => onPageChange(currentPage + 1)}
            aria-label="Next page">
            <i class="fa-sharp fa-light fa-chevron-right"></i>
          </button>
        </div>
      </div>
    `;
  }

  async _doSearch() {
    if (!this._search.trim()) { this._searchResults = null; return; }
    try {
      const resp = await authService.apiFetch(`/voice/php/api/agent-memory.php?agent_id=${this.agent.id}&search=${encodeURIComponent(this._search)}`);
      const data = await resp.json();
      this._searchResults = data.results || [];
    } catch (e) {
      this._searchResults = [];
    }
  }

  _toggleConvo(id) {
    this._expandedConvo = this._expandedConvo === id ? null : id;
  }

  _formatDuration(secs) {
    if (!secs) return '';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  _formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch (e) { return dateStr; }
  }

  _renderConversation(convo) {
    const expanded = this._expandedConvo === convo.id;
    let transcript = [];
    try { transcript = JSON.parse(convo.transcript || '[]'); } catch (e) {}
    const topics = (convo.topics || '').split(',').map(t => t.trim()).filter(Boolean);

    return html`
      <div class="convo-item">
        <div class="convo-header" @click=${() => this._toggleConvo(convo.id)}>
          <div class="convo-chevron ${expanded ? 'open' : ''}">
            <i class="fa-sharp fa-regular fa-chevron-right"></i>
          </div>
          <div class="convo-date">${this._formatDate(convo.started_at)}</div>
          <div class="convo-summary">${convo.summary || 'No summary'}</div>
          <div class="convo-duration">${this._formatDuration(convo.duration_seconds)}</div>
        </div>
        ${expanded ? html`
          <div class="convo-body">
            ${convo.summary ? html`<div style="font-size:13px;margin-bottom:10px;color:var(--color-text-secondary);"><strong>Summary:</strong> ${convo.summary}</div>` : ''}
            ${topics.length ? html`<div class="convo-topics">${topics.map(t => html`<span class="topic-tag">${t}</span>`)}</div>` : ''}
            ${transcript.length ? html`
              <div style="margin-top:10px;font-size:12px;font-weight:600;color:var(--color-text-tertiary);margin-bottom:6px;">Transcript</div>
              <div class="convo-transcript">
                ${transcript.filter(m => m.role === 'user' || m.role === 'assistant').map(m => html`
                  <div class="msg ${m.role}">${m.content}</div>
                `)}
              </div>
            ` : html`<div style="font-size:12px;color:var(--color-text-tertiary);margin-top:8px;">No transcript available</div>`}
          </div>
        ` : ''}
      </div>
    `;
  }

  render() {
    if (!this.agent) return html``;

    return html`
      ${iconStyles}

      <!-- Search -->
      <div class="mem-section">
        <div class="mem-title">
          <i class="fa-sharp fa-light fa-magnifying-glass" style="color:#5B8A72;"></i>
          Search Memory
        </div>
        <div class="mem-search">
          <input type="text" placeholder="Search past conversations..."
            .value=${this._search}
            @input=${e => { this._search = e.target.value; }}
            @keydown=${e => { if (e.key === 'Enter') this._doSearch(); }}
          />
          <button class="btn btn-primary btn-sm" @click=${() => this._doSearch()}>
            <i class="fa-sharp fa-light fa-search"></i> Search
          </button>
        </div>
        ${this._searchResults !== null ? html`
          ${this._searchResults.length === 0 ? html`<div style="font-size:12px;color:var(--color-text-tertiary);">No results found for "${this._search}"</div>` : ''}
          ${this._searchResults.length > 0 ? html`
            <div style="font-size:12px;color:var(--color-text-tertiary);margin-bottom:8px;">${this._searchResults.length} result(s) for "${this._search}"</div>
            <div class="convo-list">
              ${this._searchResults.map(r => html`
                <div class="convo-item">
                  <div class="convo-header">
                    <div class="convo-date">${this._formatDate(r.date)}</div>
                    <div class="convo-summary">${r.summary || 'No summary'}</div>
                    <div class="convo-duration">${this._formatDuration(r.duration)}</div>
                  </div>
                </div>
              `)}
            </div>
          ` : ''}
        ` : ''}
      </div>

      <!-- Memory Facts -->
      <div class="mem-section">
        <div class="mem-title">
          <i class="fa-sharp fa-light fa-brain" style="color:#A48E33;"></i>
          Key Facts
        </div>
        <div class="mem-desc">Distilled knowledge from past conversations. The AI remembers these across all calls.</div>

        ${this._loading ? html`<div class="empty-state"><i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Loading...</div>` : ''}
        ${!this._loading && this._facts.length === 0 ? html`
          <div class="empty-state">
            <i class="fa-sharp fa-light fa-lightbulb"></i>
            No memory facts yet. Facts are extracted after each conversation.
          </div>
        ` : ''}
        ${this._facts.length > 0 ? html`
          <div class="facts-grid">
            ${this._facts.map(f => html`
              <div class="fact-item">
                <span class="fact-category ${f.category}">${f.category}</span>
                <div class="fact-content">
                  <div class="fact-key">${f.key}</div>
                  <div class="fact-value">${f.value}</div>
                </div>
              </div>
            `)}
          </div>
          ${this._renderPagination(this._factPage, this._factTotal, (p) => this._goToFactPage(p))}
        ` : ''}
      </div>

      <!-- Past Conversations -->
      <div class="mem-section">
        <div class="mem-title">
          <i class="fa-sharp fa-light fa-messages" style="color:#1e6a8a;"></i>
          Past Conversations
        </div>
        <div class="mem-desc">History of all Agent One calls. Click to expand transcript.</div>

        ${this._loading ? html`<div class="empty-state"><i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Loading...</div>` : ''}
        ${!this._loading && this._conversations.length === 0 ? html`
          <div class="empty-state">
            <i class="fa-sharp fa-light fa-message-lines"></i>
            No conversations yet. Make a call to Agent One to start building memory.
          </div>
        ` : ''}
        ${this._conversations.length > 0 ? html`
          <div class="convo-list">
            ${this._conversations.map(c => this._renderConversation(c))}
          </div>
          ${this._renderPagination(this._convPage, this._convTotal, (p) => this._goToConvPage(p))}
        ` : ''}
      </div>
    `;
  }
}

customElements.define('agent-memory-tab', AgentMemoryTab);
